import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import Toast from 'react-native-root-toast';

import { MODIFY_SERVICE_RUN } from '../../../common/graphql/serviceRun';

/**
 * Vehicle choice for a service run (ServiceRunVehicleDetail and the driver day
 * actions). Choosing a vehicle counts as confirming it.
 * Returns { selectVehicle(vehicle), confirmVehicle(), loading }; onUpdated runs after a save.
 */
const useServiceRunVehicleUpdate = ({
	serviceRunId,
	adminTraining = false,
	disableActions = false,
	onUpdated,
}) => {
	const [modifyServiceRunMutation, { loading }] = useMutation(MODIFY_SERVICE_RUN);

	const updateServiceRunVehicle = useCallback(async (input) => {
		if (disableActions) {
			Toast.show('Reconnect to continue training');
			return;
		}

		try {
			await modifyServiceRunMutation({
				variables: {
					input,
				},
			});
			if (onUpdated) {
				onUpdated();
			}
		}
		catch (error) {
			Toast.show(error?.message || 'Unable to update the vehicle');
		}
	}, [disableActions, modifyServiceRunMutation, onUpdated]);

	const selectVehicle = useCallback(async (vehicle) => {
		if (!serviceRunId || !vehicle?.id) {
			return;
		}

		return updateServiceRunVehicle({
			id: serviceRunId,
			vehicle: vehicle.id,
			// a vehicle actively selected by the driver counts as confirmed
			vehicleConfirmed: true,
			...(adminTraining ? { adminTraining: true } : {}),
		});
	}, [adminTraining, serviceRunId, updateServiceRunVehicle]);

	const confirmVehicle = useCallback(async () => {
		if (!serviceRunId) {
			return;
		}

		return updateServiceRunVehicle({
			id: serviceRunId,
			vehicleConfirmed: true,
			...(adminTraining ? { adminTraining: true } : {}),
		});
	}, [adminTraining, serviceRunId, updateServiceRunVehicle]);

	return { selectVehicle, confirmVehicle, loading };
};

export default useServiceRunVehicleUpdate;
